/** @file Equips an item on a socket: its slots move to the socket bone and it stores the mount. */
import { z } from 'zod';
import type { Draft } from 'immer';
import { MOUNT_REASONS } from '@pose2d/core/mount/mountReasons.data';
import { mirrorAsymmetry } from '@pose2d/format/doc/mirrorAsymmetry';
import { throwError } from '@pose2d/format/throwError';
import type { AttachmentDoc, Doc, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { slotAttachments } from './slotAttachments';
import { slotsHolding } from './slotsHolding';

/** Why `att` cannot be mounted (§5.9; the reason words are core's MOUNT_REASONS) and what to do instead, or null. */
function mountProblem(att: AttachmentDoc): { reason: string; hint: string } | null {
  if (att.handle === undefined) return { reason: MOUNT_REASONS.noHandle, hint: 'give it a handle first (attachment.setHandle)' };
  if (att.type === 'mesh' && att.geometry.kind === 'skinned') return { reason: MOUNT_REASONS.skinned, hint: 'mount a region or a rigid mesh (mesh.setBinding rigid)' };
  return null;
}

/** True for an unmounted region or rigid mesh: it draws relative to its slot bone, so it jumps when the slot moves. */
function followsSlotBone(doc: Doc, attId: Id): boolean {
  const att = doc.attachments[attId];
  return att !== undefined && att.mount === undefined && (att.type === 'region' || att.geometry.kind === 'rigid');
}

/** True when `att` is already mounted exactly like this (equip is then a no-op, W2-31). */
function sameMount(att: AttachmentDoc, socketId: Id, fitLength: boolean): boolean {
  return att.mount?.socket === socketId && att.mount.fitLength === fitLength;
}

/** The slots holding `attId` that are not on `bone` yet (VALIDATION at `item` when no slot holds it). */
function slotsToMove(doc: Doc, attId: Id, bone: Id): Id[] {
  const slots = slotsHolding(doc, attId);
  if (slots.length === 0) throwError('VALIDATION', undefined, { path: 'item', hint: 'map the item into a slot first (skin.setEntry)' });
  return slots.filter((id) => (doc.slots[id]?.bone ?? bone) !== bone);
}

/**
 * MOUNT_INVALID at `item` when a slot about to move also holds another attachment mounted on a different socket
 * (R2-17): that mount would be left in a slot off its socket bone. Unmounted slot mates move with the slot.
 */
function assertNoOtherMount(doc: Doc, item: { id: Id; name: string }, socketId: Id, slots: readonly Id[]): void {
  for (const slotId of slots) {
    const other = slotAttachments(doc, slotId)
      .map((id) => (id === item.id ? undefined : doc.attachments[id]))
      .find((att) => att?.mount !== undefined && att.mount.socket !== socketId);
    if (other?.mount === undefined) continue;
    const on = doc.sockets[other.mount.socket]?.name ?? other.mount.socket;
    const hint = `slot "${doc.slots[slotId]?.name ?? slotId}" also holds "${other.name}", mounted on socket "${on}": unequip it first (socket.unequip)`;
    throwError('MOUNT_INVALID', { attachment: item.name, reason: 'another mounted item shares the slot' }, { path: 'item', hint });
  }
}

/** Moves the listed slots onto `bone`; INFO `{ slot }` at `item` for each one whose unmounted slot mates jump with it. */
function moveSlots(draft: Draft<Doc>, ctx: CommandContext, attId: Id, slots: readonly Id[], bone: Id): void {
  for (const id of slots) {
    const slot = draft.slots[id];
    if (!slot) continue;
    if (slotAttachments(draft, id).some((other) => other !== attId && followsSlotBone(draft, other))) {
      const message = `slot "${slot.name}" also holds unmounted attachments; they now follow the socket bone and may move`;
      ctx.warnings.push({ code: 'INFO', message, params: { slot: slot.name }, path: 'item' });
    }
    slot.bone = bone;
  }
}

/** socket.equip: moves the item's slots to the socket bone and stores the mount (an identical mount is a no-op). */
export const command = defineCommand({
  name: 'socket.equip',
  description:
    'Equip item attachment `item` (needs a handle; regions and rigid meshes only) on `socket`: every slot holding it moves to the socket bone and the item stores mount { socket, fitLength } (fitLength scales the handle to the grip length); refused while a slot that would move holds another item mounted on a different socket (unequip that one first); an INFO names each moved slot whose other unmounted attachments now follow the socket bone; returns MIRROR_ASYMMETRY warnings for the item.',
  input: z.object({ item: z.string().min(1), socket: z.string().min(1), fitLength: z.boolean().default(false) }).strict(),
  apply(draft, input, ctx) {
    const attId = ctx.resolve('attachment', input.item);
    const socketId = ctx.resolve('socket', input.socket);
    const att = draft.attachments[attId] ?? throwError('NOT_FOUND', { kind: 'attachment', ref: attId });
    const socket = draft.sockets[socketId] ?? throwError('NOT_FOUND', { kind: 'socket', ref: socketId });
    const problem = mountProblem(att);
    if (problem !== null) throwError('MOUNT_INVALID', { attachment: att.name, reason: problem.reason }, { path: 'item', hint: problem.hint });
    if (!sameMount(att, socketId, input.fitLength)) {
      const slots = slotsToMove(draft, attId, socket.bone);
      assertNoOtherMount(draft, { id: attId, name: att.name }, socketId, slots);
      moveSlots(draft, ctx, attId, slots, socket.bone);
      att.mount = { socket: socketId, fitLength: input.fitLength };
      draft.meta.updatedAt = ctx.now;
    }
    for (const w of mirrorAsymmetry(draft)) if (w.params?.attachment === att.name) ctx.warnings.push(w);
  },
});
